import React, { FC, useState, useEffect, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid
} from '@material-ui/core';
import { ShopOrder } from 'kiolyn-api';
import { useShop } from '../../stores/ShopStore';
import { useObserver } from 'mobx-react-lite';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker
} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import {
  displayDateFormat,
  storeDateFormat
} from '../../domain/models/Constants';

const useStyles = makeStyles(theme => ({
  table: {
    width: '100%'
  },
  thHeader: {
    border: 'none',
    color: theme.palette.primary.main
  },
  thBody: {
    border: 'none'
  },
  dateTime: {
    minWidth: theme.spacing(30),
    maxWidth: 320,
    marginRight: 10
  },
  dateTimeContainer: {
    marginTop: theme.spacing(1)
  }
}));

interface ShopHistoryProps {}

const ShopHistory: FC<ShopHistoryProps> = () => {
  const shopStore = useShop();
  const classes = useStyles();
  const [state, setState] = useState({
    history: new Array<ShopOrder>(),
    startDate: moment().startOf('days'),
    endDate: moment().startOf('days')
  });

  const handleBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string
  ) => {
    const target = event.currentTarget;
    const value = target.value;
    const dateValue = moment(value, 'MM/DD/YY', true);
    if (
      !dateValue.isValid() ||
      (key === 'startDate' && dateValue.isAfter(state.endDate)) ||
      (key === 'endDate' && dateValue.isBefore(state.startDate))
    ) {
      setState({
        ...state,
        [key]: moment().startOf('days')
      });
    }
  };

  const handleDateChange = (start: moment.Moment, end: moment.Moment) => {
    if (!start?.isValid() || !end?.isValid() || start.isAfter(end)) {
      return;
    }

    shopStore
      .loadShopHistory(
        start.format(storeDateFormat),
        end.format(storeDateFormat)
      )
      .then(value => {
        let shopHistory = new Array<ShopOrder>();
        if (value) {
          shopHistory = value;
        }

        setState({
          history: shopHistory,
          startDate: start,
          endDate: end
        });
      })
      .catch(() =>
        setState({
          history: [],
          startDate: start,
          endDate: end
        })
      );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => handleDateChange(state.startDate, state.endDate), []);

  const startDatePicker = useObserver(() => (
    <MuiPickersUtilsProvider key="start" utils={MomentUtils}>
      <KeyboardDatePicker
        className={classes.dateTime}
        label="Start date"
        format={displayDateFormat}
        value={state.startDate}
        onChange={d => handleDateChange(d as moment.Moment, state.endDate)}
        onBlur={e => handleBlur(e, 'startDate')}
      />
    </MuiPickersUtilsProvider>
  ));

  const endDatePicker = useObserver(() => (
    <MuiPickersUtilsProvider key="end" utils={MomentUtils}>
      <KeyboardDatePicker
        className={classes.dateTime}
        label="End date"
        format={displayDateFormat}
        value={state.endDate}
        onChange={d => handleDateChange(state.startDate, d as moment.Moment)}
        onBlur={e => handleBlur(e, 'endDate')}
      />
    </MuiPickersUtilsProvider>
  ));

  return useObserver(() => (
    <Fragment>
      <Grid container direction="row" className={classes.dateTimeContainer}>
        {startDatePicker} {endDatePicker}
      </Grid>
      <Table className={classes.table} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell className={classes.thHeader}>ID</TableCell>
            <TableCell className={classes.thHeader}>DATE</TableCell>
            <TableCell className={classes.thHeader}>STATUS</TableCell>
            <TableCell className={classes.thHeader}>TOTAL POINTS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {state.history.map(row => (
            <TableRow key={row.id}>
              <TableCell className={classes.thBody} component="th" scope="row">
                {row.id}
              </TableCell>
              <TableCell className={classes.thBody}>
                {moment(row.id.substr(0, 6), storeDateFormat).format(
                  displayDateFormat
                )}
              </TableCell>
              <TableCell className={classes.thBody}>{row.status}</TableCell>
              <TableCell className={classes.thBody}>
                {row.totalPoints}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Fragment>
  ));
};

export default ShopHistory;
